import { assert } from '../app/logger.js';
import { Dialog } from '../controls/dialog.js';

/**
 * Invite friends dialog, aka share trip dialog.
 */
class GoingWithDialog extends Dialog {
  initialize() {
    super.initialize();

    this.#changeReasons = new Set();

    this.listControl.oninput = val => {
      val ? this.invitesChanged('input') : this.invitesReverted('input');
    };
    this.listControl.onitemchange = vals => {
      vals && vals.length > 0
        ? this.invitesChanged('list')
        : this.invitesReverted('list');
    };

    this.items = [];
  }

  show() {
    this.#changeReasons = new Set();
    this.acceptButton.hidden = true;
    this.cancelButton.textContent = 'Close';

    this.value = this._value;
    this.listControl.value = '';

    super.show();

    if (!this._value || this._value.length == 0) {
      this.listControl.focus();
    }
  }

  static tagName = 'goingwith-dialog';
  static titleMarkup = `Invite friends`;

  static bodyMarkup = `
    <form class='default share-dialog'>
      <div class='shares-container'></div>
      <div class='autocomplete-container'>
        <friend-list-control label='Invite'></friend-list-control>
      </div>
    </form>
  `;

  static get selectors() {
    const Form = 'form';
    const SharesContainer = '.shares-container';
    const Cocampers = `${SharesContainer} div.list-item-friend`;
    const RevokeButtons = '.revoke-invite-button';
    return Object.assign({}, super.selectors, {
      Form,
      Cocampers,
      CocamperEmails: `${Cocampers} .list-item-friend-email`,
      CocamperNames: `${Cocampers} .list-item-friend-name`,
      CocamperStatuses: `${Cocampers} .list-item-friend-isaccepted`,
      CocamperAt(i) {
        return `${Cocampers}:nth-of-type(${i + 1})`;
      },
      RejectedCocampers: `${Cocampers}[rejected]`,
      RevokeButtons,
      RevokeButtonAt(i) {
        return `${this.CocamperAt(i)} ${RevokeButtons}`;
      },
      RevokeButtonBy(inviteHash) {
        return `${Cocampers} ${RevokeButtons}[invite-hash="${inviteHash}"]`;
      },
      ListControl: 'friend-list-control',
      SharesContainer,
    });
  }

  static get elements() {
    let s = this.selectors;
    return {
      allRevokeButtons: s.RevokeButtons,
      listControl: s.ListControl,
      form: s.Form,
    };
  }

  static get buttons() {
    return {
      accept: {
        label: 'Update',
      },
      cancel: {
        label: 'Close',
        default: true,
      },
    };
  }

  checkValidity() {
    return this.form.reportValidity();
  }

  get data() {
    let revokedInviteHashes = [
      ...this.bodyElement.querySelectorAll('[rejected] [invite-hash]'),
    ].map(e => e.getAttribute('invite-hash'));

    return {
      invitees: this.listControl.valueAsInvitees,
      revokedInviteHashes,
    };
  }

  invitesChanged(reason) {
    assert(
      new Set(['input', 'list', 'rejected']).has(reason),
      `Invalid reason ${reason}`
    );

    this.#changeReasons.add(reason);

    this.acceptButton.hidden = false;
    this.cancelButton.textContent = 'Cancel';
  }

  invitesReverted(reason) {
    assert(
      new Set(['input', 'list', 'rejected']).has(reason),
      `Invalid reason ${reason}`
    );

    this.#changeReasons.delete(reason);
    if (this.#changeReasons.size == 0) {
      this.acceptButton.hidden = true;
      this.cancelButton.textContent = 'Close';
    }
  }

  get value() {
    return this._value;
  }
  set value(cocampers) {
    this._value = cocampers;
    let sharesContainer = this.bodyElement.querySelector('.shares-container');
    if (cocampers.length == 0) {
      sharesContainer.innerHTML = '';
      return;
    }

    let cocampersUI = ``;
    for (let cocamper of cocampers) {
      let name = cocamper.inviteeName;
      let email = cocamper.inviteeEmail;
      let avatar = cocamper.inviteeAvatar;
      let status = '';
      if (cocamper.status == 'pending') {
        status = `Haven't yet<br> RSVP`;
      } else if (cocamper.status == 'rejected') {
        status = `Rejected`;
      } else if (cocamper.rights && cocamper.rights.includes('all')) {
        status = `Admin`;
      }

      cocampersUI += `
        <div class='list-item-friend'>
          <div class='list-item-friend-block'>
            <img src='${avatar}' class='list-item-friend-avatar'>
            <div class='list-item-friend-info'>
              <div class='list-item-friend-name'>${name}</div>
              <div class='list-item-friend-email'>${email}</div>
            </div>
          </div>
          <div class='list-item-friend-isaccepted'><span>${status}</span></div>
          <div class='sqbutton revoke-invite-button' title='Revoke invite'
               invite-hash='${cocamper.inviteHash}'></div>
        </div>
      `;
    }
    cocampersUI = `
      <fieldset class='friends-list'>
        <legend>Who is going</legend>
        ${cocampersUI}
      </fieldset>
    `;
    sharesContainer.innerHTML = cocampersUI;

    this.readonly = this._readonly; // re-set readonly to update UI

    sharesContainer.addEventListener(
      'click',
      ev => {
        let inviteHash = ev.target.getAttribute('invite-hash');
        if (inviteHash) {
          ev.target.parentNode.setAttribute('rejected', 'true');
          this.invitesChanged('rejected');
        }
      },
      false
    );
  }

  get readonly() {
    return this._readonly;
  }
  set readonly(v) {
    this._readonly = v;
    for (let btn of this.allRevokeButtons) {
      btn.hidden = v;
    }
    this.bodyElement.querySelector('.autocomplete-container').hidden = v;
  }

  get valueAsArray() {
    return this.items.map(v => v.name);
  }

  #changeReasons;
}

GoingWithDialog.defineCustomElement();

export { GoingWithDialog };
